import { useIntl } from '@umijs/max';
import { Modal } from 'antd';
import { createStyles } from 'antd-style';
import { useEffect, useState } from 'react';
import { ActionDock, ActionDockButton } from './ActionDock';
import { DownloadOutlined } from './icon/tabler';
import VideoPlayer from './VideoPlayer';
import { downloadAsset, filenameFromURL } from '../utils/mediaDownload';

export type MediaPreviewType = 'image' | 'video';
export interface MediaPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  url?: string | null;
  type?: MediaPreviewType;
  title?: string;
  filenamePrefix?: string;
  showDownload?: boolean;
}
const useStyles = createStyles(({ css, token }) => ({
  layout: css`display: flex; flex-direction: column; align-items: center; gap: 12px; height: calc(100dvh - 48px); min-height: 0; padding-bottom: env(safe-area-inset-bottom, 0px);`,
  stage: css`display: flex; align-items: center; justify-content: center; position: relative; flex: 1; min-height: 0; width: 100%; container-type: size; > img { display: block; max-width: 100%; max-height: 100%; object-fit: contain; }`,
  error: css`padding: 16px; background: ${token.colorBgElevated}; color: ${token.colorTextSecondary}; border-radius: ${token.borderRadiusLG}px;`,
}));

// A preview shell only: all video rendering and playback belong to VideoPlayer.
export default function MediaPreviewDialog({
  open,
  onClose,
  url,
  type = 'image',
  title,
  filenamePrefix,
  showDownload = true,
}: MediaPreviewDialogProps) {
  const { styles } = useStyles();
  const intl = useIntl();
  const [ratio, setRatio] = useState(16 / 9);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  useEffect(() => {
    setRatio(16 / 9);
    setFailed(false);
    setDownloadFailed(false);
  }, [open, url, type]);
  if (!open) return null;
  const downloadLabel = intl.formatMessage({
    id: 'canvasOps.preview.download',
    defaultMessage: 'Download',
  });
  return (
    <Modal
      open
      centered
      width="calc(100vw - 48px)"
      className="qm-media-preview-dialog nowheel nopan nodrag"
      style={{ maxWidth: 'calc(100vw - 24px)', paddingBottom: 0 }}
      styles={{
        container: {
          background: 'transparent',
          border: 0,
          boxShadow: 'none',
          padding: 0,
        },
        header: { display: 'none' },
        body: { minHeight: 0 },
      }}
      title={
        title ||
        intl.formatMessage({
          id: 'canvas.aiToolbar.previewZoom',
          defaultMessage: 'Preview',
        })
      }
      closable={false}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      keyboard
      mask={{ closable: true }}
    >
      <div className={styles.layout}>
        <div
          className={styles.stage}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          {!url || failed ? (
            <div className={styles.error} role="status">
              {intl.formatMessage({
                id: failed
                  ? `canvas.refs.unavailable.${type}`
                  : type === 'video'
                    ? 'canvasOps.preview.noVideo'
                    : 'canvasOps.preview.noImage',
                defaultMessage: 'Media unavailable',
              })}
            </div>
          ) : type === 'video' ? (
            <VideoPlayer
              key={url}
              src={url}
              autoPlay
              loop
              preview
              exclusive
              onError={() => setFailed(true)}
              onDimensions={(w, h) => {
                if (w > 0 && h > 0) setRatio(w / h);
              }}
              style={{
                width: `min(100cqw, calc(100cqh * ${ratio}))`,
                height: 'auto',
                aspectRatio: ratio,
              }}
            />
          ) : (
            <img
              src={url}
              alt={title || filenamePrefix || ''}
              onError={() => setFailed(true)}
            />
          )}
        </div>
        {downloadFailed && (
          <div className={styles.error} role="status">
            {intl.formatMessage({
              id: 'canvasOps.preview.downloadFailed',
              defaultMessage: 'Download failed. Try again.',
            })}
          </div>
        )}
        <ActionDock
          wrap
          onClose={onClose}
          closeLabel={intl.formatMessage({
            id: 'canvas.common.close',
            defaultMessage: 'Close',
          })}
          aria-label={intl.formatMessage({
            id: 'canvas.aiToolbar.previewZoom',
            defaultMessage: 'Preview',
          })}
        >
          {showDownload && (
            <ActionDockButton
              icon={<DownloadOutlined />}
              disabled={!url}
              loading={downloading}
              onClick={async () => {
                setDownloading(true);
                setDownloadFailed(false);
                try {
                  const prefix = filenamePrefix || title;
                  const ext =
                    url?.match(
                      /\.(png|jpe?g|webp|gif|avif|svg|bmp|mp4|webm|mov|m4v)(?:[?#]|$)/i,
                    )?.[1] || (type === 'video' ? 'mp4' : 'png');
                  const filename = prefix
                    ? `${prefix.replace(/[^\w一-龥.-]+/g, '_').slice(0, 60)}.${ext}`
                    : filenameFromURL(url || undefined);
                  await downloadAsset(url || undefined, filename);
                } catch {
                  setDownloadFailed(true);
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloadLabel}
            </ActionDockButton>
          )}
        </ActionDock>
      </div>
    </Modal>
  );
}
